/** Shopping list + item endpoints. */

import { ApiClient } from "./client";

export type ShoppingPriority = "low" | "normal" | "high" | "urgent";
export type ShoppingCategory =
  | "produce"
  | "dairy"
  | "bakery"
  | "meat"
  | "frozen"
  | "pantry"
  | "drinks"
  | "household"
  | "personal"
  | "other";

/** Aisle-walk order — matches backend ``SHOPPING_CATEGORIES`` enum order. */
export const SHOPPING_CATEGORIES: readonly ShoppingCategory[] = [
  "produce",
  "bakery",
  "dairy",
  "meat",
  "frozen",
  "pantry",
  "drinks",
  "household",
  "personal",
  "other",
];

export const SHOPPING_PRIORITIES: readonly ShoppingPriority[] = [
  "low",
  "normal",
  "high",
  "urgent",
];

export interface ItemResponse {
  id: string;
  list_id: string;
  position: number;
  name: string;
  quantity: string;
  unit: string;
  category: ShoppingCategory | null;
  priority: ShoppingPriority;
  brand: string | null;
  description: string | null;
  price_cents: number | null;
  tag_ids: string[];
  /** Presigned URL minted on each GET; ``null`` when the item has no
   *  image. The raw S3 object key is never exposed to the client. */
  image_url: string | null;
  /** Hex color ``#rrggbb``; ``null`` falls back to the parent list's
   *  color, then to the screen accent. */
  color: string | null;
  checked_at: string | null;
  created_at: string;
  updated_at: string;
}

export type RecordStatus = "active" | "archived" | "deleted";

/** Caller-relative scope for ``GET /shopping-lists``. ``"mine"`` returns
 *  only lists the caller owns, ``"shared"`` only lists shared *with*
 *  the caller, ``"all"`` (default) returns both. */
export type ShoppingListScope = "mine" | "shared" | "all";

/** Embedded payload that decorates a resource when the caller is a
 *  viewer rather than the owner. ``null`` on owner-owned rows. */
export interface SharedByView {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

/** Effective access of the caller on a shared resource. ``"owner"``
 *  is the implicit access of the row's owner; only viewers carry
 *  ``"view"`` / ``"edit"``. */
export type EffectiveAccess = "view" | "edit" | "owner";

export interface ShoppingListResponse {
  id: string;
  user_id: string;
  name: string;
  items: ItemResponse[];
  created_at: string;
  updated_at: string;
  status: RecordStatus;
  pinned: boolean;
  section_id: string | null;
  position: number;
  store: string | null;
  currency: string | null;
  budget_cap_cents: number | null;
  color: string | null;
  /** Owner of the list — same as ``user_id`` for backwards compat,
   *  surfaced explicitly so the UI doesn't conflate "the API caller"
   *  with "the owner". */
  owner_id: string;
  /** Non-null on lists shared *with* the caller. Null for lists the
   *  caller owns. */
  shared_by: SharedByView | null;
  /** Effective access the caller has on this row. */
  my_access: EffectiveAccess;
}

export interface ShoppingListsResponse {
  lists: ShoppingListResponse[];
}

export interface ShoppingListSectionResponse {
  id: string;
  name: string;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface ShoppingListSectionListResponse {
  sections: ShoppingListSectionResponse[];
}

export interface UpdateListPrefPayload {
  pinned?: boolean;
  /** ``null`` clears section assignment. */
  section_id?: string | null;
  position?: number;
}

export interface AddItemRequest {
  name: string;
  quantity?: string;
  unit?: string;
  category?: ShoppingCategory | null;
  priority?: ShoppingPriority;
  brand?: string | null;
  description?: string | null;
  price_cents?: number | null;
  tags?: readonly string[];
  color?: string | null;
}

/** PATCH body — omit a field to leave it alone, send ``null`` to clear. */
export interface UpdateItemRequest {
  name?: string;
  quantity?: string;
  unit?: string;
  priority?: ShoppingPriority;
  category?: ShoppingCategory | null;
  brand?: string | null;
  description?: string | null;
  price_cents?: number | null;
  tags?: readonly string[];
  color?: string | null;
}

/** PATCH body for ``/shopping-lists/{id}/settings``. */
export interface UpdateListSettingsRequest {
  store?: string | null;
  currency?: string | null;
  budget_cap_cents?: number | null;
  color?: string | null;
}

export interface ClearCheckedResponse {
  removed: number;
}

export interface CopyFromRecipeResponse {
  added: number;
}

export const shoppingApi = (client: ApiClient) => ({
  listLists(
    status?: RecordStatus,
    scope?: ShoppingListScope,
  ): Promise<ShoppingListsResponse> {
    const params = new URLSearchParams();
    if (status !== undefined) params.set("status", status);
    if (scope !== undefined) params.set("scope", scope);
    const qs = params.toString();
    const path = qs.length === 0
      ? "/shopping-lists"
      : `/shopping-lists?${qs}`;
    return client.request<ShoppingListsResponse>(path, { method: "GET" });
  },
  archiveList(id: string): Promise<ShoppingListResponse> {
    return client.request<ShoppingListResponse>(`/shopping-lists/${id}/archive`, {
      method: "POST",
    });
  },
  restoreList(id: string): Promise<ShoppingListResponse> {
    return client.request<ShoppingListResponse>(`/shopping-lists/${id}/restore`, {
      method: "POST",
    });
  },
  updateListPref(id: string, patch: UpdateListPrefPayload): Promise<ShoppingListResponse> {
    return client.request<ShoppingListResponse>(`/shopping-lists/${id}/pref`, {
      method: "PATCH",
      json: patch,
    });
  },
  listSections(): Promise<ShoppingListSectionListResponse> {
    return client.request<ShoppingListSectionListResponse>(
      "/shopping-list-sections",
      { method: "GET" },
    );
  },
  createSection(name: string): Promise<ShoppingListSectionResponse> {
    return client.request<ShoppingListSectionResponse>("/shopping-list-sections", {
      method: "POST",
      json: { name },
    });
  },
  renameSection(id: string, name: string): Promise<ShoppingListSectionResponse> {
    return client.request<ShoppingListSectionResponse>(
      `/shopping-list-sections/${id}`,
      { method: "PATCH", json: { name } },
    );
  },
  reorderSections(ids: readonly string[]): Promise<ShoppingListSectionListResponse> {
    return client.request<ShoppingListSectionListResponse>(
      "/shopping-list-sections/reorder",
      { method: "PATCH", json: { section_ids: [...ids] } },
    );
  },
  deleteSection(id: string): Promise<void> {
    return client.request<void>(`/shopping-list-sections/${id}`, {
      method: "DELETE",
    });
  },
  getList(id: string): Promise<ShoppingListResponse> {
    return client.request<ShoppingListResponse>(`/shopping-lists/${id}`, { method: "GET" });
  },
  createList(name: string): Promise<ShoppingListResponse> {
    return client.request<ShoppingListResponse>("/shopping-lists", {
      method: "POST",
      json: { name },
    });
  },
  renameList(id: string, name: string): Promise<ShoppingListResponse> {
    return client.request<ShoppingListResponse>(`/shopping-lists/${id}`, {
      method: "PATCH",
      json: { name },
    });
  },
  updateListSettings(
    id: string,
    patch: UpdateListSettingsRequest,
  ): Promise<ShoppingListResponse> {
    return client.request<ShoppingListResponse>(
      `/shopping-lists/${id}/settings`,
      { method: "PATCH", json: patch },
    );
  },
  deleteList(id: string): Promise<void> {
    return client.request<void>(`/shopping-lists/${id}`, { method: "DELETE" });
  },
  clearChecked(id: string): Promise<ClearCheckedResponse> {
    return client.request<ClearCheckedResponse>(`/shopping-lists/${id}/clear-checked`, {
      method: "POST",
    });
  },
  copyFromRecipe(listId: string, recipeId: string): Promise<CopyFromRecipeResponse> {
    return client.request<CopyFromRecipeResponse>(
      `/shopping-lists/${listId}/copy-from-recipe`,
      { method: "POST", json: { recipe_id: recipeId } },
    );
  },
  addItem(listId: string, body: AddItemRequest): Promise<ItemResponse> {
    return client.request<ItemResponse>(`/shopping-lists/${listId}/items`, {
      method: "POST",
      json: body,
    });
  },
  updateItem(listId: string, itemId: string, body: UpdateItemRequest): Promise<ItemResponse> {
    return client.request<ItemResponse>(`/shopping-lists/${listId}/items/${itemId}`, {
      method: "PATCH",
      json: body,
    });
  },
  deleteItem(listId: string, itemId: string): Promise<void> {
    return client.request<void>(`/shopping-lists/${listId}/items/${itemId}`, {
      method: "DELETE",
    });
  },
  checkItem(listId: string, itemId: string): Promise<ItemResponse> {
    return client.request<ItemResponse>(
      `/shopping-lists/${listId}/items/${itemId}/check`,
      { method: "POST" },
    );
  },
  uncheckItem(listId: string, itemId: string): Promise<ItemResponse> {
    return client.request<ItemResponse>(
      `/shopping-lists/${listId}/items/${itemId}/uncheck`,
      { method: "POST" },
    );
  },
  /** Flat GET for cross-entity link resolution; no list_id required. */
  getItem(itemId: string): Promise<ItemResponse> {
    return client.request<ItemResponse>(`/shopping-items/${itemId}`, {
      method: "GET",
    });
  },
  uploadItemImage(
    listId: string,
    itemId: string,
    file: File,
  ): Promise<ItemResponse> {
    const form = new FormData();
    form.append("file", file);
    return client.request<ItemResponse>(
      `/shopping-lists/${listId}/items/${itemId}/image`,
      { method: "POST", body: form },
    );
  },
  deleteItemImage(listId: string, itemId: string): Promise<ItemResponse> {
    return client.request<ItemResponse>(
      `/shopping-lists/${listId}/items/${itemId}/image`,
      { method: "DELETE" },
    );
  },
});
